// html2canvas helpers for capturing one section of the app (the Overview
// export) as an image.
//
// Two things break a plain html2canvas(el) call in this app:
//  1. Colors. html2canvas 1.4 cannot parse the CSS color() function and
//     throws "Attempting to parse an unsupported color function". Chrome
//     reports any color-mix(in srgb, ...) value (the Strike tokens in
//     styles/strike.css, e.g. --primary-edge) as color(srgb r g b / a).
//  2. Clipping. The page scrolls inside a fixed-height flex panel next to the
//     side menu, so the element is cut to one screen, and its on-screen
//     position does not match where it lands in html2canvas's copy of the
//     page.
//  3. Animations. The copy starts every CSS animation over, so the tab's
//     entrance animation (strike-rise in strike.css, from opacity 0) is at
//     its first frame when the copy is drawn - the capture came out white.
//  4. Digits. strike.css sets tabular-nums on the whole page, and html2canvas
//     draws with the canvas font, which has no such setting: it draws each
//     word (a date's "11", "/", "2" are three) at the place the page laid it
//     out with fixed-width digits, in the face's narrower plain digits. Dates
//     came out "11 /2/26" and right-aligned numbers stopped short.
//
// fullCaptureOptions(el) returns the html2canvas options that fix all four
// on the cloned page (the live page is not touched): colors become rgba(),
// animations are off, digits are laid out as they are drawn, and the element
// is laid out alone at the top left with nothing clipping it. planPages()
// then cuts the capture into PDF pages between rows rather than at fixed
// heights.

const SRGB = /color\(srgb\s+([-\d.e]+)\s+([-\d.e]+)\s+([-\d.e]+)(?:\s*\/\s*([-\d.e]+%?))?\s*\)/g;

const channel = (v) => Math.round(Math.min(1, Math.max(0, parseFloat(v))) * 255);
const alpha = (a) => {
    if (a == null) return 1;
    const n = a.endsWith('%') ? parseFloat(a) / 100 : parseFloat(a);
    return Math.min(1, Math.max(0, n));
};

// Every color(srgb ...) inside a computed value (a box-shadow or gradient can
// hold several) as rgba(); anything else comes back unchanged.
export function srgbToRgba(value) {
    if (!value || value.indexOf('color(') === -1) return value;
    return value.replace(SRGB, (_, r, g, b, a) => `rgba(${channel(r)}, ${channel(g)}, ${channel(b)}, ${alpha(a)})`);
}

const COLOR_PROPS = [
    'color', 'background-color', 'background-image',
    'border-top-color', 'border-right-color', 'border-bottom-color', 'border-left-color',
    'outline-color', 'text-decoration-color', 'box-shadow', 'text-shadow',
];

function fixCloneColors(doc, el) {
    const view = doc.defaultView;
    if (!view) return;
    [el, ...el.querySelectorAll('*')].forEach((node) => {
        const cs = view.getComputedStyle(node);
        COLOR_PROPS.forEach((p) => {
            const v = cs.getPropertyValue(p);
            if (v && v.indexOf('color(') !== -1) node.style.setProperty(p, srgbToRgba(v), 'important');
        });
    });
}

// The element and its ancestors up to <html>: siblings hidden, nothing
// clipped, no fixed heights - so the element sits at 0,0 at its full height.
function isolateClone(el) {
    const set = (node, styles) => Object.entries(styles).forEach(([k, v]) => node.style.setProperty(k, v, 'important'));
    set(el, { margin: '0', height: 'auto', 'min-height': '0', 'max-height': 'none' });
    for (let node = el; node.parentElement; node = node.parentElement) {
        const parent = node.parentElement;
        Array.from(parent.children).forEach((sib) => {
            if (sib !== node && !/^(STYLE|LINK|SCRIPT|META|TITLE|HEAD)$/.test(sib.tagName)) set(sib, { display: 'none' });
        });
        set(parent, { display: 'block', overflow: 'visible', height: 'auto', 'min-height': '0', 'max-height': 'none', margin: '0', padding: '0', border: '0', position: 'static', transform: 'none' });
    }
}

// onLayout, when given, gets measureLayout() of the clone exactly as it is
// drawn, for planPages().
export function fullCaptureOptions(el, onLayout) {
    const width = el.scrollWidth;
    const height = el.scrollHeight;
    return {
        x: 0, y: 0, width, height,
        windowWidth: width, windowHeight: height,
        scrollX: 0, scrollY: 0,
        onclone: (doc, clonedEl) => {
            // [data-export-drop]: on the page, left out of the capture
            const still = doc.createElement('style');
            still.textContent = '*, *::before, *::after { animation: none !important; transition: none !important; font-variant-numeric: normal !important; }' +
                ' [data-export-drop] { display: none !important; }';
            doc.head.appendChild(still);
            fixCloneColors(doc, clonedEl);
            isolateClone(clonedEl);
            if (onLayout) onLayout(measureLayout(clonedEl));
        },
    };
}

// ── Page breaks ─────────────────────────────────────────────────────────
// Cutting the capture into pages at a fixed height slices table rows and
// charts in half. measureLayout() records, in CSS px from the element's top,
// what must not be cut (table rows, charts, images, lines of text), the cards
// and every table's header and rows. planPages() then ends each page between
// rows, moves a card that would start near the foot of a page onto the next
// page when it fits there whole, keeps a table's header with at least two
// rows and its last row with the one before, and repeats a table's header at
// the top of the page it continues on.

const UNCUT = /^(TR|IMG|CANVAS|svg|SVG|VIDEO|INPUT|SELECT|TEXTAREA|BUTTON)$/;

const ownText = (node) => Array.from(node.childNodes).some((n) => n.nodeType === 3 && n.nodeValue.trim());

export function measureLayout(el) {
    const view = el.ownerDocument.defaultView;
    const base = el.getBoundingClientRect();
    const span = (node) => {
        const r = node.getBoundingClientRect();
        return [r.top - base.top, r.bottom - base.top];
    };
    const atoms = [];
    const cards = [];
    const tables = [];

    // clip: the visible band of the nearest scroll box above, so content
    // scrolled out of sight there does not block a break where it would be.
    const walk = (node, clip) => {
        for (let child = node.firstElementChild; child; child = child.nextElementSibling) {
            const r = child.getBoundingClientRect();
            if (r.width <= 0 || r.height <= 0) continue;
            let [top, bottom] = [r.top - base.top, r.bottom - base.top];
            if (clip) { top = Math.max(top, clip[0]); bottom = Math.min(bottom, clip[1]); }
            const tag = child.tagName;
            const uncut = UNCUT.test(tag);
            if (bottom > top && (uncut || !child.firstElementChild || ownText(child))) atoms.push([top, bottom]);
            if (uncut) continue; // a row, chart or image is one piece
            const cs = view.getComputedStyle(child);
            if (tag === 'TABLE') {
                const head = child.tHead && child.tHead.getBoundingClientRect().height > 0 ? span(child.tHead) : null;
                const rows = Array.from(child.rows)
                    .filter((row) => row.parentNode !== child.tHead && row.getBoundingClientRect().height > 0)
                    .map(span);
                if (rows.length) tables.push({ top, bottom, head, rows });
            } else if (tag === 'DIV' && r.width >= base.width * 0.6 && r.height >= 40) {
                const rounded = parseFloat(cs.borderTopLeftRadius) >= 6;
                const edged = parseFloat(cs.borderTopWidth) > 0 || (cs.boxShadow && cs.boxShadow !== 'none');
                if (rounded && edged) cards.push({ top, bottom });
            }
            let inner = clip;
            if (cs.overflowY !== 'visible' && child.scrollHeight > child.clientHeight + 1) {
                inner = [Math.max(top, clip ? clip[0] : -Infinity), Math.min(bottom, clip ? clip[1] : Infinity)];
            }
            walk(child, inner);
        }
    };
    walk(el, null);
    return { width: base.width, height: base.height, atoms, cards, tables };
}

const EPS = 0.5;

// Pages for a capture of `layout`, each pageH CSS px tall:
// [{ top, bottom, header: [top, bottom] | null }], where header is a table
// header to draw above the page's content.
export function planPages(layout, pageH) {
    const { height, atoms, cards, tables } = layout;

    // Uncut pieces merged into solid blocks; a break is fine anywhere outside one.
    const blocks = [];
    atoms.slice().sort((a, b) => a[0] - b[0]).forEach(([t, b]) => {
        const last = blocks[blocks.length - 1];
        if (last && t < last[1] - EPS) last[1] = Math.max(last[1], b);
        else blocks.push([t, b]);
    });
    // The lowest clean break in [lo, hi], or null.
    const lastClean = (lo, hi) => {
        const inside = blocks.find(([t, b]) => t < hi - EPS && b > hi + EPS);
        const y = inside ? inside[0] : hi;
        return y >= lo ? y : null;
    };

    // Breaks to avoid when there is another choice: a table's header with
    // fewer than two rows under it, a table's last row on its own, a card's
    // title alone at the foot of a page, a short table split at all.
    const avoid = [];
    tables.forEach(({ top, bottom, rows }) => {
        if (rows.length < 4) { avoid.push([top, bottom]); return; }
        avoid.push([top, rows[1][1]]);
        avoid.push([rows[rows.length - 2][1], bottom]);
    });
    cards.forEach(({ top, bottom }) => {
        let keep = top + Math.min(bottom - top, 90);
        const first = tables.find((t) => t.top >= top - EPS && t.top - top < 120);
        if (first) keep = Math.max(keep, first.rows[Math.min(1, first.rows.length - 1)][1]);
        avoid.push([top, Math.min(keep, bottom)]);
    });
    const avoided = (y) => avoid.find(([a, b]) => y > a + EPS && y < b - EPS);

    const sortedCards = cards.slice().sort((a, b) => a.top - b.top);
    const pickBreak = (y, limit, avail) => {
        // A card that starts on this page but runs past it goes to the next
        // page whole when it fits there and this page is already well used;
        // one taller than a page still moves if it would start in the last fifth.
        for (const c of sortedCards) {
            if (c.top <= y + EPS || c.top >= limit || c.bottom <= limit) continue;
            const left = limit - c.top;
            if ((c.bottom - c.top <= pageH && left <= avail * 0.45) || left <= avail * 0.2) {
                const at = lastClean(y + avail * 0.3, c.top);
                if (at != null) return at;
            }
        }
        // The lowest clean break in the bottom half that nothing asks to avoid
        const lo = y + avail * 0.5;
        for (let hi = limit, at = lastClean(lo, hi); at != null; at = lastClean(lo, hi)) {
            const hit = avoided(at);
            if (!hit) return at;
            hi = hit[0];
        }
        const any = lastClean(y + avail * 0.25, limit);
        return any != null ? any : limit;
    };

    // The header of the table a break falls inside, to repeat on the next page
    const headerAt = (y) => {
        const t = tables
            .filter((tb) => tb.head && tb.head[1] <= y + EPS && tb.bottom > y + 2 && tb.rows.some((r) => r[0] >= y - EPS))
            .sort((a, b) => b.head[0] - a.head[0])[0];
        return t && t.head[1] - t.head[0] <= pageH * 0.2 ? t.head : null;
    };

    const pages = [];
    let y = 0;
    let header = null;
    while (y < height - 1) {
        const avail = pageH - (header ? header[1] - header[0] : 0);
        const limit = y + avail;
        let end = limit >= height ? height : pickBreak(y, limit, avail);
        if (end <= y + 1) end = Math.min(height, limit);
        pages.push({ top: y, bottom: end, header });
        header = end < height ? headerAt(end) : null;
        y = end;
    }
    return pages;
}
